import { msg } from "@lingui/core/macro";
import { type Component, type ComponentProps, createMemo, For, Show } from "solid-js";
import { Bell, Leaf, Mail, ShieldCheck, Smartphone, Sparkles } from "lucide-solid";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@famidex/shared";
import { useI18n } from "@/providers/i18n-provider";
import { showToast } from "@/components/ui/toast";

type Resp = components["schemas"]["NotificationPreferencesResponse"];
type ErrorModel = components["schemas"]["ErrorModel"];

const CHANNELS: { id: "push" | "email" | "in_app"; icon: Component<ComponentProps<"svg">> }[] = [
  { id: "push", icon: Smartphone },
  { id: "email", icon: Mail },
  { id: "in_app", icon: Bell },
];

/**
 * "Configuración de Alertas" — pantalla FamiDex re-vestida sobre las
 * preferencias de notificación existentes (`/api/notifications/preferences`,
 * matriz canal × categoría). Las de seguridad (transaccional) siempre están
 * activas y no se muestran como toggle.
 */
export function AlertSettings() {
  const { t } = useI18n();

  const prefs = createApiQuery<Resp, ErrorModel>(
    () => ["notifications", "preferences"],
    async () => api.GET("/api/notifications/preferences"),
    () => ({ staleTime: 30_000 }),
  );

  const updateMutation = createApiMutation(
    async (vars: components["schemas"]["UpdatePreferenceDto"]) =>
      api.POST("/api/notifications/preferences", { body: vars }),
  );

  const prefMap = createMemo(() => {
    const map = new Map<string, boolean>();
    for (const p of prefs.data?.preferences ?? []) {
      map.set(`${p.channel}:${p.category}`, p.enabled);
    }
    return map;
  });

  const isEnabled = (channel: string, category: string) =>
    prefMap().get(`${channel}:${category}`) ?? true;

  const toggle = async (channel: string, category: string, enabled: boolean) => {
    try {
      await updateMutation.mutateAsync({ channel, category, enabled });
      await prefs.refetch();
    } catch {
      showToast(t(msg`Couldn't save that setting.`), "error");
    }
  };

  const channelLabel = (c: string) =>
    c === "push" ? t(msg`Push (mobile)`) : c === "email" ? t(msg`Email`) : t(msg`In-app`);

  const sections: {
    category: "product" | "marketing";
    title: string;
    description: string;
    icon: Component<ComponentProps<"svg">>;
  }[] = [
    {
      category: "product",
      title: t(msg`Community & collection`),
      description: t(msg`Likes, comments, tags, new releases and daily rewards.`),
      icon: Leaf,
    },
    {
      category: "marketing",
      title: t(msg`News & newsletters`),
      description: t(msg`FamiDex updates, tips and seasonal events.`),
      icon: Sparkles,
    },
  ];

  return (
    <div class="space-y-6">
      <header class="text-center">
        <h1 class="text-2xl font-bold tracking-tight text-primary sm:text-3xl">
          {t(msg`Alert settings`)}
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">
          {t(msg`Keep your collection connected, at your own pace.`)}
        </p>
      </header>

      <Show
        when={!prefs.isLoading}
        fallback={
          <div class="space-y-6">
            <Skeleton class="h-48 w-full rounded-xl" />
            <Skeleton class="h-48 w-full rounded-xl" />
          </div>
        }
      >
        <For each={sections}>
          {(section) => (
            <Card class="relative overflow-hidden">
              <CardContent class="p-6">
                <div class="mb-5 flex items-center gap-3">
                  <span class="grid h-10 w-10 place-items-center rounded-full bg-accent/15 text-accent">
                    <section.icon class="h-5 w-5" />
                  </span>
                  <div>
                    <h2 class="font-semibold text-foreground">{section.title}</h2>
                    <p class="text-sm text-muted-foreground">{section.description}</p>
                  </div>
                </div>
                <div class="space-y-4">
                  <For each={CHANNELS}>
                    {(channel) => (
                      <div class="flex items-center justify-between border-t border-border/60 pt-4 first:border-t-0 first:pt-0">
                        <span class="flex items-center gap-2 text-sm text-foreground">
                          <channel.icon class="h-4 w-4 text-muted-foreground" />
                          {channelLabel(channel.id)}
                        </span>
                        <Switch
                          checked={isEnabled(channel.id, section.category)}
                          onCheckedChange={(v) => toggle(channel.id, section.category, v)}
                        />
                      </div>
                    )}
                  </For>
                </div>
              </CardContent>
            </Card>
          )}
        </For>

        <Card class="border-accent/20 bg-accent/5">
          <CardContent class="flex items-start gap-3 p-5">
            <ShieldCheck class="mt-0.5 h-5 w-5 shrink-0 text-accent" />
            <p class="text-sm text-muted-foreground">
              {t(
                msg`Security alerts (sign-ins, password changes) are always on and can't be turned off.`,
              )}
            </p>
          </CardContent>
        </Card>
      </Show>
    </div>
  );
}
